import { useEffect, useState } from "react";

/**
 * useState that survives a refresh by mirroring to localStorage.
 *
 * Reads once on mount (lazy initialiser, so the read does not run on every
 * render) and writes on every change. Every access is guarded: localStorage
 * throws in Safari private mode and when the quota is full, and a sidebar
 * remembering its width is never worth breaking a page over - on failure this
 * silently degrades to ordinary in-memory state.
 *
 * @param {string} key    storage key, namespaced by the caller (attar.*)
 * @param {*} initialValue used when nothing is stored yet or the read fails
 */
export default function usePersistentState(key, initialValue) {
    const [value, setValue] = useState(() => {
        try {
            const stored = window.localStorage.getItem(key);
            return stored === null ? initialValue : JSON.parse(stored);
        } catch {
            return initialValue;
        }
    });

    useEffect(() => {
        try {
            window.localStorage.setItem(key, JSON.stringify(value));
        } catch {
            // storage unavailable - keep working, just do not persist
        }
    }, [key, value]);

    return [value, setValue];
}
